/** Automatic encrypted project backup after local git commits. */

import { invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { getCloudSession, hasCloudBackupEntitlement } from "./cloudAuth";
import { COMMIT_SAVE } from "./commitMessages";
import { commitProjectNow } from "./tauri/git";
import { isTauri } from "./runtime";
import { syncFromCloud } from "./cloudSync";
import { withCloudActivity } from "./cloudActivity";
import {
  base64ToBytes,
  ensureCloudProject,
  fetchCanonicalBackupStatus,
  forgetCloudProjectId,
  noteLocalSnapshotPush,
  recordPackUpload,
  shouldForceThickPack,
  uploadSnapshotPack,
} from "./cloudSnapshot";
import {
  isValidPackWire,
  packPrefixPreview,
  reportCorruptPack,
} from "./packIntegrity";
import { createTimeoutHandle } from "./timeoutHandle";
import { trimmedOrEmpty, trimmedOrNull } from "./trimmedString";

const CLOUD_BACKUP_EVENT = "zw:cloud-backup";

type ProjectCommittedPayload = {
  root: string;
  oid: string;
};

type BundleExport = {
  bundleBase64: string;
  gitHeadOid: string;
  gitBaseOid?: string | null;
};

const CHRONICLE_TARGET_REF = "refs/zw-chronicle/log";

function apiErrorMessage(body: unknown, fallback: string): string {
  if (typeof body === "object" && body !== null && "error" in body) {
    const err = (body as { error?: unknown }).error;
    if (typeof err === "string" && err.length > 0) return err;
  }
  return fallback;
}

function folderFallbackName(root: string): string {
  const parts = root.split(/[/\\]/).filter((value) => value.length > 0);
  const last = parts.at(-1);
  if (typeof last !== "undefined" && last.length > 0) return last;
  return "Project";
}

/**
 * Best-effort, independent of the manuscript upload above (#105 Phase 1):
 * pushes the chronicle's own new commits. Never throws — a failure here must
 * not affect manuscript backup, and there's always a next commit to retry on.
 */
async function uploadChronicleIfNeeded(
  root: string,
  cloudId: string,
): Promise<void> {
  try {
    const config = await invoke<{
      cloudSync?: { lastUploadedChronicleOid?: string | null };
    }>("read_zw_config", { rootPath: root });
    const since = trimmedOrNull(config.cloudSync?.lastUploadedChronicleOid);

    const bundle = await invoke<BundleExport>("export_chronicle_bundle", {
      rootPath: root,
      sinceOid: since,
    });
    if (
      bundle.gitHeadOid.length === 0 ||
      (since !== null && bundle.gitHeadOid === since)
    ) {
      return;
    }

    const res = await uploadSnapshotPack(cloudId, {
      bytes: base64ToBytes(bundle.bundleBase64),
      gitHeadOid: bundle.gitHeadOid,
      gitBaseOid: trimmedOrNull(bundle.gitBaseOid),
      source: "desktop",
      targetRef: CHRONICLE_TARGET_REF,
    });
    if (res.ok === false) return;

    await invoke("patch_cloud_sync", {
      rootPath: root,
      lastUploadedOid: null,
      lastPulledOid: null,
      divergenceCloudOid: null,
      divergenceChoice: null,
      clearDivergence: false,
      lastUploadedChronicleOid: bundle.gitHeadOid,
      lastPulledChronicleOid: null,
    });
  } catch (error) {
    console.warn("[cloudBackup] chronicle upload failed", error);
  }
}

async function cloudBackupActive(): Promise<boolean> {
  return hasCloudBackupEntitlement();
}

async function uploadSnapshot(
  root: string,
  oid: string,
  opts: { forceThick?: boolean } = {},
): Promise<void> {
  let clientProjectId: string | null = null;
  let last: string | null = null;
  let lastPulled: string | null = null;
  let name = "";
  try {
    const config = await invoke<{
      cloudProjectId?: string | null;
      cloudSync?: {
        lastUploadedOid?: string | null;
        lastPulledOid?: string | null;
      };
      metadata?: { title?: string };
    }>("read_zw_config", { rootPath: root });
    clientProjectId = trimmedOrNull(config.cloudProjectId);
    last = trimmedOrNull(config.cloudSync?.lastUploadedOid);
    lastPulled = trimmedOrNull(config.cloudSync?.lastPulledOid);
    name = trimmedOrEmpty(config.metadata?.title);
  } catch {
    /* fall through */
  }
  // Do not skip when last === oid: a corrupt cloud pack still needs a thick
  // re-upload, and the server returns full_pack_required so we can heal it.
  if (clientProjectId === null) {
    console.warn(
      "[cloudBackup] missing cloudProjectId; reopen the project to generate one",
    );
    return;
  }
  if (name.length === 0) {
    name = folderFallbackName(root);
  }

  const pulledForeign =
    lastPulled !== null &&
    last !== null &&
    lastPulled.length > 0 &&
    last.length > 0 &&
    lastPulled !== last;
  const forceThick =
    opts.forceThick === true ||
    pulledForeign ||
    shouldForceThickPack(clientProjectId, last);

  const postPack = async (
    cloudId: string,
    sinceOid: string | null,
  ): Promise<{
    res: Response;
    headOid: string;
    gitBaseOid: string | null;
  }> => {
    const bundle = await invoke<BundleExport>("export_project_bundle", {
      rootPath: root,
      sinceOid,
    });
    const headOid = bundle.gitHeadOid.length > 0 ? bundle.gitHeadOid : oid;
    const gitBaseOid = trimmedOrNull(bundle.gitBaseOid);
    const bytes = base64ToBytes(bundle.bundleBase64);
    if (isValidPackWire(bytes) === false) {
      reportCorruptPack("rejected_upload", {
        cloudProjectId: cloudId,
        clientProjectId,
        byteSize: bytes.byteLength,
        prefix: packPrefixPreview(bytes),
        ...(sinceOid !== null ? { sinceOid } : {}),
      });
      throw new Error("export_project_bundle did not produce a git packfile");
    }
    const res = await uploadSnapshotPack(cloudId, {
      bytes,
      gitHeadOid: headOid,
      gitBaseOid,
      source: "desktop",
    });
    return { res, headOid, gitBaseOid };
  };

  let cloudId = await ensureCloudProject(clientProjectId, name);
  const sinceOid: string | null = forceThick ? null : last;
  let { res, headOid, gitBaseOid } = await postPack(cloudId, sinceOid);

  if (res.status === 404) {
    forgetCloudProjectId(clientProjectId);
    cloudId = await ensureCloudProject(clientProjectId, name);
    ({ res, headOid, gitBaseOid } = await postPack(cloudId, sinceOid));
  }

  // Cloud could not merge into the stored pack. Resend a self-contained pack
  // with no base; the server replaces the corrupt backup. No user prompt.
  if (await isFullPackRequired(res)) {
    reportCorruptPack("detected", {
      cloudProjectId: cloudId,
      clientProjectId,
      source: "upload_full_pack_required",
    });
    ({ res, headOid, gitBaseOid } = await postPack(cloudId, null));
  }

  if (res.ok === false) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      apiErrorMessage(err, `Snapshot upload failed (${res.status})`),
    );
  }

  recordPackUpload(clientProjectId, gitBaseOid === null);
  noteLocalSnapshotPush(root, headOid);
  await invoke("write_cloud_sync_oid", { rootPath: root, oid: headOid });
  await uploadChronicleIfNeeded(root, cloudId);
  notifyBackupChanged();
}

/**
 * On project open: if cloud says the canonical pack is unreadable, push a
 * thick pack immediately (same silent heal as a 409 during upload).
 */
export async function ensureCanonicalBackup(root: string): Promise<void> {
  if (isTauri() === false) return;
  if ((await cloudBackupActive()) === false) return;

  let clientProjectId: string | null;
  let name: string;
  try {
    const config = await invoke<{
      cloudProjectId?: string | null;
      metadata?: { title?: string };
    }>("read_zw_config", { rootPath: root });
    clientProjectId = trimmedOrNull(config.cloudProjectId);
    name = trimmedOrEmpty(config.metadata?.title);
  } catch {
    return;
  }
  if (clientProjectId === null) return;
  if (name.length === 0) {
    name = folderFallbackName(root);
  }

  let cloudId: string;
  try {
    cloudId = await ensureCloudProject(clientProjectId, name);
  } catch (error) {
    console.warn("[cloudBackup] ensure project for backup check failed", error);
    return;
  }

  let status: Awaited<ReturnType<typeof fetchCanonicalBackupStatus>>;
  try {
    status = await fetchCanonicalBackupStatus(cloudId);
  } catch (error) {
    console.warn("[cloudBackup] backup status check failed", error);
    return;
  }
  if (status !== "unreadable") return;

  const oid = await invoke<string | null>("git_head_oid", {
    rootPath: root,
  }).catch(() => null);
  if (oid === null || oid.length === 0) return;

  reportCorruptPack("detected", {
    cloudProjectId: cloudId,
    clientProjectId,
    source: "open_canonical_check",
  });
  reportCorruptPack("heal", {
    cloudProjectId: cloudId,
    clientProjectId,
    source: "open_canonical_check",
  });
  console.info("[cloudBackup] cloud pack unreadable; uploading full pack");
  await withCloudActivity(root, () =>
    uploadSnapshot(root, oid, { forceThick: true }),
  );
}

async function isFullPackRequired(res: Response): Promise<boolean> {
  if (res.ok) return false;
  if (res.status === 409) return true;
  try {
    const body = (await res.clone().json()) as { error?: string };
    return body.error === "full_pack_required";
  } catch {
    return false;
  }
}

type Pending = {
  root: string;
  oid: string;
};

let pending: Pending | null = null;
const uploadTimer = createTimeoutHandle();
let unlisten: UnlistenFn | null = null;
let uploading = false;

function notifyBackupChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CLOUD_BACKUP_EVENT));
}

export { CLOUD_BACKUP_EVENT };

async function flushPending(): Promise<void> {
  if (pending === null || uploading) return;
  const job = pending;
  pending = null;
  uploading = true;
  try {
    if ((await cloudBackupActive()) === false) return;
    await withCloudActivity(job.root, () => uploadSnapshot(job.root, job.oid));
  } catch (error) {
    console.warn("[cloudBackup] upload failed", error);
  } finally {
    uploading = false;
    // `pending` may be set during the await above; TS can't see that.
    const again = pending as Pending | null;
    if (again !== null) {
      pending = null;
      scheduleUpload(again.root, again.oid);
    }
  }
}

/** Coalesce rapid autosaves so pack export/IPC is not on every keystroke pause. */
const UPLOAD_DEBOUNCE_MS = 8_000;

function scheduleUpload(root: string, oid: string): void {
  pending = { root, oid };
  uploadTimer.schedule(UPLOAD_DEBOUNCE_MS, () => {
    void flushPending();
  });
}

/**
 * Listen for local project commits and upload an encrypted snapshot.
 * Rapid commits while an upload is in flight collapse to the latest tip.
 */
export async function installCloudBackupListener(): Promise<() => void> {
  if (isTauri() === false) return () => {};
  if (unlisten !== null) {
    return () => {
      unlisten?.();
      unlisten = null;
    };
  }

  unlisten = await listen<ProjectCommittedPayload>(
    "project-committed",
    (event) => {
      const root = event.payload?.root;
      const oid = event.payload?.oid;
      if (
        root === null ||
        root.length === 0 ||
        oid === null ||
        oid.length === 0
      ) {
        return;
      }
      if (getCloudSession() === null) return;
      scheduleUpload(root, oid);
    },
  );

  return () => {
    unlisten?.();
    unlisten = null;
    uploadTimer.clear();
    pending = null;
  };
}

/** Queue an upload of the current git HEAD (used when there is nothing new to commit). */
export async function uploadHeadNow(root: string): Promise<void> {
  if (isTauri() === false) return;
  const oid = await invoke<string | null>("git_head_oid", {
    rootPath: root,
  }).catch(() => null);
  if (oid === null || oid.length === 0) return;
  scheduleUpload(root, oid);
}

/**
 * Manual save / click-to-sync: pull, commit dirty worktree, then upload HEAD.
 * Must wait for the commit before uploading — otherwise we push the previous tip
 * and in-file edits never leave the device (restore/rename did, because those
 * paths committed before upload more reliably).
 */
export async function kickCloudSync(root: string): Promise<void> {
  if (isTauri() === false) return;
  if (getCloudSession() !== null) {
    try {
      await syncFromCloud(root, "precommit");
    } catch (error) {
      console.warn("[cloudBackup] pre-save sync failed", error);
    }
  }
  await commitProjectNow(root, COMMIT_SAVE);
  await uploadHeadNow(root);
}
